import React, { Suspense, lazy } from 'react';
import { Header, Segment, Grid, Dimmer, Loader, Dropdown, List, Tab, Menu, Message } from "semantic-ui-react";
import {
  Router,
  Switch,
  Route,
  Redirect,
  Link,
} from 'react-router-dom';
import { Helmet } from "react-helmet";
import * as Cookies from 'es-cookie';

import TrainGrid from './trainGrid';
import StationList from './stationList';
// Modals are code-split: they are only needed once a user opens one.
const AboutModal = lazy(() => import('./aboutModal'));
const StationModal = lazy(() => import('./stationModal'));
const TwitterModal = lazy(() => import('./twitterModal'));

import Footer from './footer';
import history from './history';
import API_BASE from './apiBase';

// Only the Semantic UI component stylesheets this app actually uses, instead
// of the full semantic.min.css (roughly halves the CSS payload).
import 'semantic-ui-css/components/reset.min.css';
import 'semantic-ui-css/components/site.min.css';
import 'semantic-ui-css/components/button.min.css';
import 'semantic-ui-css/components/checkbox.min.css';
import 'semantic-ui-css/components/dimmer.min.css';
import 'semantic-ui-css/components/divider.min.css';
import 'semantic-ui-css/components/dropdown.min.css';
import 'semantic-ui-css/components/grid.min.css';
import 'semantic-ui-css/components/header.min.css';
import 'semantic-ui-css/components/icon.min.css';
import 'semantic-ui-css/components/image.min.css';
import 'semantic-ui-css/components/input.min.css';
import 'semantic-ui-css/components/label.min.css';
import 'semantic-ui-css/components/list.min.css';
import 'semantic-ui-css/components/loader.min.css';
import 'semantic-ui-css/components/menu.min.css';
import 'semantic-ui-css/components/message.min.css';
import 'semantic-ui-css/components/modal.min.css';
import 'semantic-ui-css/components/popup.min.css';
import 'semantic-ui-css/components/segment.min.css';
import 'semantic-ui-css/components/statistic.min.css';
import 'semantic-ui-css/components/tab.min.css';
import 'semantic-ui-css/components/table.min.css';
import 'semantic-ui-css/components/transition.min.css';
// App-wide overrides; must come after the Semantic UI stylesheets above.
import './app.scss';

const ROUTES_API_URL = `${API_BASE}/routes`;
const STOPS_API_URL = `${API_BASE}/stops`;

class App extends React.Component {
  constructor(props) {
    super(props);
    const favStationsStr = Cookies.get('favStations');
    const favStations = favStationsStr?.split(",");
    // The server inlines the current /routes payload as props so the trains
    // grid can render immediately instead of waiting for a fetch.
    const initial = props.initialRoutes;
    this.state = {
      trains: initial?.routes || {},
      timestamp: initial?.timestamp,
      loading: false,
      blogPost: initial?.blog_post || {},
      favStations: new Set(favStations),
    };
    if (favStationsStr) {
      Cookies.set('favStations', favStationsStr, {expires: 365});
    }
  }

  componentWillUnmount() {
    clearInterval(this.timer);
  }

  componentDidMount() {
    if (Object.keys(this.state.trains).length === 0) {
      this.setState({loading: true});
    }
    this.fetchData();
    this.timer = setInterval(() => this.fetchData(), 15000);
  }

  fetchData() {
    fetch(ROUTES_API_URL)
      .then(response => response.json())
      .then(data => this.setState({ trains: data.routes, timestamp: data.timestamp, blogPost: data.blog_post, loading: false }));

    fetch(STOPS_API_URL)
      .then(response => response.json())
      .then(data => this.setState({ stations: data.stops }));
  }

  handlePinStation = (stationId) => {
    const { favStations } = this.state;
    favStations.add(stationId);
    this.setState({favStations: favStations});
    Cookies.set('favStations', [...favStations].join(','), {expires: 365});
  }

  handleUnpinStation = (stationId) => {
    const { favStations } = this.state;
    favStations.delete(stationId);
    this.setState({favStations: favStations});
    Cookies.set('favStations', [...favStations].join(','), {expires: 365});
  }

  renderLoading() {
    const { loading } = this.state;
    if (loading) {
      return(
        <Dimmer active>
          <Loader inverted></Loader>
        </Dimmer>
      )
    }
  }

  renderAbout() {
    return (
      <React.Fragment>
        <Suspense fallback={null}><AboutModal open={true} /></Suspense>
        <Tab panes={this.renderPanes()} activeIndex="0" />
      </React.Fragment>
    );
  }

  renderTwitter() {
    return (
      <React.Fragment>
        <Suspense fallback={null}><TwitterModal open={true} /></Suspense>
        <Tab panes={this.renderPanes()} activeIndex="0" />
      </React.Fragment>
    );
  }

  renderStations() {
    const { trains, stations, favStations } = this.state;

    return (
      <StationList trains={trains} stations={stations} favStations={favStations} selectedStationId={this.selectedStationId} />
    );
  }

  renderStation(stationId) {
    const { trains, stations, favStations } = this.state;
    const selectedStation = stations.find((s) => s.id === stationId);
    const stationsObj = {};
    stations.forEach((s) => {
      stationsObj[s.id] = s;
    })
    return (
      <React.Fragment>
        <Suspense fallback={null}>
          <StationModal open={true} stations={stationsObj} isFavStation={favStations.has(stationId)}
            handlePinStation={this.handlePinStation} handleUnpinStation={this.handleUnpinStation}
            trains={trains} selectedStation={selectedStation}
          />
        </Suspense>
        {
          this.renderStations()
        }
      </React.Fragment>
    );
  }

  renderTrains(selectedTrain) {
    const { trains, stations } = this.state;
    return (
      <TrainGrid selectedTrain={selectedTrain} trains={trains} stations={stations} />
    )
  }

  renderPanes() {
    const { trains, stations } = this.state;
    const trainKeys = Object.keys(trains);
    return [
      { menuItem: <Menu.Item as={Link} to='/' key='trains'>Trains</Menu.Item>,
        render: () =>
          <Tab.Pane inverted>
              { this.renderLoading() }
              { trainKeys.length > 0 && stations &&
                <Switch>
                  <Route path='/trains/:id/:direction?/:tripId?' render={(props) => {
                    if (props.match.params.id && trainKeys.includes(props.match.params.id)) {
                      return this.renderTrains(props.match.params.id);
                    } else {
                      return (<Redirect to="/" />);
                    }
                  }} />
                  <Route path='/trains' render={() => {
                    return this.renderTrains(null);
                  }} />
                  <Route path='/' exact render={() => {
                    return this.renderTrains(null);
                  }} />
                  <Route path='/about' render={() => {
                    return this.renderTrains(null);
                  }} />
                  <Route path='/twitter' render={() => {
                    return this.renderTrains(null);
                  }} />
                  <Route render={() => <Redirect to="/" /> } />
                </Switch>
              }
          </Tab.Pane>
      },
      { menuItem: <Menu.Item as={Link} to='/stations' key='stations'>Stations</Menu.Item>,
        render: () =>
          <Tab.Pane inverted>
            { this.renderLoading() }
              { trainKeys.length > 0 && stations &&
                <Switch>
                  <Route path='/stations/:id' render={(props) => {
                    this.selectedStationId = props.match.params.id;
                    const stationIds = stations.map((s) => s.id);
                    if (props.match.params.id && stationIds.includes(props.match.params.id)) {
                      return this.renderStation(props.match.params.id);
                    } else {
                      return (<Redirect to="/" />);
                    }
                  }} />
                  <Route path='/stations' render={() => {
                    this.selectedStationId = null;
                    return this.renderStations();
                  }} />
                  <Route render={() => <Redirect to="/stations" /> } />
                </Switch>
              }
          </Tab.Pane>
      },
    ];
  }

  render() {
    const { trains, timestamp, blogPost, loading, stations } = this.state;
    const trainKeys = Object.keys(trains);
    return (
      <Router history={history}>
        <Segment inverted vertical className='header-segment'>
          <Header inverted as='h1' style={{color: "#b0b1b2"}}>
            Subway Now <sup style={{color: "#888889"}}>lite</sup>
            <Header.Subheader>
              New York City Subway Status Page
                <sup>[<Link to='/about'>?</Link>]</sup>
            </Header.Subheader>
          </Header>
          <Helmet>
            <title>Subway Now lite (formerly goodservice.io) - New York City Subway Status Page</title>
            <meta property="og:title" content="Subway Now lite" />
            <meta name="twitter:title" content="Subway Now lite" />
            <link rel="canonical" href="https://lite.subwaynow.app" />
            <meta property="og:url" content="https://lite.subwaynow.app" />
            <meta name="twitter:url" content="https://lite.subwaynow.app" />
            <meta name="Description" content="New York City Subway Status Page. Track active MTA subway trains, check for delays, compare headways and travel times, view real-time route maps and estimated arrival times for each subway route and at any station. Formerly known as goodservice.io." />
          </Helmet>
        </Segment>
        <Segment inverted vertical className='blogpost-segment'>
          <Header as='h5' inverted>
            Blog Post: <a href={blogPost.link} target="_blank">{blogPost.title}</a>
          </Header>
        </Segment>
        <Segment basic className='content-segment'>
          <Switch>
            <Route path="/trains" render={() => <Tab menu={{pointing: true, secondary: true, inverted: true}} panes={this.renderPanes()} activeIndex="0" />} />
            <Route path="/" exact render={() => <Tab menu={{pointing: true, secondary: true, inverted: true}} panes={this.renderPanes()} activeIndex="0" />} />
            <Route path="/stations" render={() => <Tab menu={{pointing: true, secondary: true, inverted: true}} panes={this.renderPanes()} activeIndex="1" />} />
            <Route path='/about' render={() => {
              return this.renderAbout();
            }} />
            <Route path='/twitter' render={() => {
              return this.renderTwitter();
            }} />
            <Route render={() => <Redirect to="/" />} />
          </Switch>
        </Segment>
        <Footer timestamp={timestamp} />
      </Router>
    );
  }
}

export default App;
